import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { Table } from 'antd';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import TableAddDrawer from '../TableAddDrawer';

import { getIdMap, includesTerm, safeExtractFromObject } from '../../../../helpers/helpers';
import useValidateMaxSelect from '../../../../common/hooks/useValidateMaxSelect';
import colors from '../../../../constants/Colors';

function EquipmentTableAddDrawer({
  equipment,
  visible,
  onClose,
  onSubmit,
  selected: initialSelected = [],
  maxExistingAllowed,
  configProps,
}) {
  const settings = useSelector((state) => safeExtractFromObject(state, 'settings.company.settings'));
  const projects = useSelector((state) => safeExtractFromObject(state, 'projects.projects'));
  const projectIdMap = useMemo(() => getIdMap(projects), [projects]);
  const [searchStr, setSearchStr] = useState();
  const [selected, setSelected] = useState(initialSelected);
  const { addEquipmentTableSorting } = configProps;

  const cols = [
    {
      title: 'Name',
      dataIndex: 'name',
    },
    {
      title: 'ID',
      dataIndex: 'code',
    },
    {
      title: 'Status',
      dataIndex: 'status',
    },
    {
      title: 'Available?',
      dataIndex: 'availability',
      render: (bool) => {
        if (bool) {
          return <CheckOutlined style={{ color: colors.ONTRACCR_SUCCESS }} />;
        }
        return <CloseOutlined style={{ color: colors.ONTRACCR_ERROR }} />;
      },
    },
    {
      title: 'Current Assignment',
      dataIndex: 'currentProjectId',
      render: (projectId, record) => {
        if (projectId) {
          return projectIdMap[projectId]?.name;
        }
        if (record.currentLocationText) {
          return record.currentLocationText;
        }
        return null;
      },
    },
  ];

  const sortEquipment = useCallback((equipmentList, sorting) => (
    equipmentList.sort((a, b) => {
      switch (sorting) {
        case 'name-asc':
          if (!a.name) return 1;
          if (!b.name) return -1;
          return a.name.localeCompare(b.name);
        case 'name-desc':
          if (!a.name) return -1;
          if (!b.name) return 1;
          return b.name.localeCompare(a.name);
        case 'id-asc':
          if (!a.code) return 1;
          if (!b.code) return -1;
          return a.code.localeCompare(b.code, undefined, { numeric: true });
        case 'id-desc':
          if (!a.code) return -1;
          if (!b.code) return 1;
          return b.code.localeCompare(a.code, undefined, { numeric: true });
        default:
          return 0;
      }
    })
  ), []);

  const data = useMemo(() => {
    const sortedEquipment = sortEquipment([...equipment], addEquipmentTableSorting);

    if (!searchStr) return sortedEquipment;

    return sortedEquipment.filter((eq) => (
      includesTerm(eq.name, searchStr)
      || includesTerm(eq.code, searchStr)
    ));
  }, [equipment, searchStr, addEquipmentTableSorting, sortEquipment]);

  useEffect(() => {
    setSelected(initialSelected);
  }, [initialSelected]);

  useEffect(() => {
    if (!visible) {
      setSearchStr();
      setSelected([]);
    }
  }, [visible]);

  const onSubmitClicked = useCallback(() => {
    const isValid = useValidateMaxSelect({
      maxExistingAllowed,
      selected,
      type: 'equipment',
    });

    if (isValid) onSubmit(selected);
  }, [
    selected,
    onSubmit,
    maxExistingAllowed,
  ]);

  const onSelectRow = useCallback((record, checked) => {
    if (checked) {
      setSelected(selected.concat(record));
    } else {
      setSelected(selected.filter((s) => s.id !== record.id));
    }
  }, [selected, setSelected]);

  const selectedRowKeys = useMemo(() => (
    selected.map((s) => s.id)
  ), [selected]);

  const onHeaderRow = useCallback(() => {
    if (!settings?.uxRedesignPt1) return null;
    return {
      className: 'ontraccr-table-header',
    };
  }, [settings]);

  return (
    <TableAddDrawer
      title="Add Equipment"
      visible={visible}
      onClose={onClose}
      onSearch={setSearchStr}
      onSubmit={onSubmitClicked}
      searchStr={searchStr}
      destroyOnClose
      width={800}
    >
      <Table
        size="small"
        dataSource={data}
        columns={cols}
        pagination={false}
        rowKey="id"
        rowSelection={{
          fixed: true,
          hideSelectAll: true,
          preserveSelectedRowKeys: true,
          onSelect: onSelectRow,
          selectedRowKeys,
          getCheckboxProps: () => ({
            className: settings?.uxRedesignPt1 ? 'ontraccr-checkbox' : '',
          }),
        }}
        onHeaderRow={onHeaderRow}
      />
    </TableAddDrawer>
  );
}

/* eslint-disable react/forbid-prop-types */
EquipmentTableAddDrawer.propTypes = {
  equipment: PropTypes.array,
  selected: PropTypes.array,
  visible: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  maxExistingAllowed: PropTypes.number,
  configProps: PropTypes.object,
};

EquipmentTableAddDrawer.defaultProps = {
  equipment: [],
  selected: [],
  visible: false,
  maxExistingAllowed: null,
  configProps: {},
};

export default EquipmentTableAddDrawer;
